import { Box, Divider, HStack, VStack } from '@chakra-ui/react';

import { formatAmount } from 'common/formatAmount';
import { useInternationalTransferPayoutFees } from 'common/internationalTransfers/hooks/useInternationalTransferPayoutFees';
import { getFormattedExchangeRate } from 'common/internationalTransfers/utils/getFormattedExchangeRate';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Tooltip from 'components/_core/Tooltip';
import { VAT_RATE } from 'helpers/amount';

import { DetailedTransactionFragment } from '../graphql/fragment/detailedTransaction.gql';
import locales from './locales';

const InternationalTransferFeesTable: React.FC<{
  transaction: DetailedTransactionFragment;
}> = ({ transaction }) => {
  const { fee, internationalTransferPayout } = transaction;
  const { tooltipText } = useInternationalTransferPayoutFees(
    internationalTransferPayout?.fee.currency,
  );
  if (!internationalTransferPayout) {
    return null;
  }

  const { targetAmountFloat, targetCurrency } = internationalTransferPayout;

  const feeWithoutVat = fee / VAT_RATE;

  return (
    <>
      <Divider size="lg" />
      <Box paddingX="space-24" paddingY="space-40">
        <VStack align="stretch" divider={<Divider />} spacing="space-16">
          <HStack
            alignItems="top"
            justifyContent="space-between"
            spacing="space-16"
          >
            <Text variant="secondary">{locales.targetAmountLabel}</Text>
            <HStack spacing="space-4">
              <Text variant="primary">
                {formatAmount({
                  currency: targetCurrency,
                  currencyDisplay: 'code',
                  forcePlusSign: false,
                  value: targetAmountFloat,
                })}
              </Text>
            </HStack>
          </HStack>

          <HStack
            alignItems="top"
            justifyContent="space-between"
            spacing="space-16"
          >
            <Text variant="secondary">{locales.exchangeRate}</Text>
            <HStack spacing="space-4">
              <Text variant="primary">
                {getFormattedExchangeRate({
                  rate: internationalTransferPayout.exchangeRate,
                  sourceCurrency: internationalTransferPayout.sourceCurrency,
                  targetCurrency: internationalTransferPayout.targetCurrency,
                })}
              </Text>
            </HStack>
          </HStack>

          <HStack alignItems="top" justify="space-between" spacing="space-16">
            <Text variant="secondary">{locales.fee}</Text>
            <Box textAlign="right">
              <HStack spacing="space-4">
                <Tooltip description={tooltipText} marginX="space-16">
                  <Box paddingX="space-4">
                    <SunshineIcon color="grey.600" name="information" />
                  </Box>
                </Tooltip>
                <Text variant="primary">
                  {locales.formatString(
                    locales.feeWithVat,
                    formatAmount({ type: 'payout', value: fee }),
                  )}
                </Text>
              </HStack>
              <Text size="sm" variant="light">
                {locales.formatString(
                  locales.feeWithoutVat,
                  formatAmount({ type: 'payout', value: feeWithoutVat }),
                )}
              </Text>
            </Box>
          </HStack>
        </VStack>
      </Box>
    </>
  );
};

export default InternationalTransferFeesTable;
